import React, { useState, useEffect, Fragment } from 'react'

import {
  CardContent,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  useTheme,
  useMediaQuery,
  withStyles
} from '@material-ui/core'
import FullWidthTabs from '../../../../UI/Tabs/Tabs'
import Button from '../../../../UI/Button/Button'
import StructureTable from './StructureTable/StructureTable'
import AppraisalActionControls from './AppraisalActionControls/AppraisalActionControls'
import { Autocomplete } from '@material-ui/lab'
import { getSelectvalue } from '../../../../../helpers/utils'
import { getCompetencyAssessorOptions } from '../../../../../helpers/appraisal'
import withTranslate from 'react-redux-multilingual/lib/withTranslate'
import ParentScore from './ParentScore/ParentScore'

function StructureTabs({
  appraisalInfo,
  appraisalData = [],
  // competencyTypes,
  inputChanged,
  translate,
  openCommentModal,
  calculateButtonClicked,
  submitButtonClicked,
  cancelButtonClicked,
  showActionTo,
  actionToButtonClicked,
  showSumUp,
  sumUpButtonClicked,
  savedList,
  isAdmin,
  loadingStates
}) {
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('xs'))

  const [currentTabIndex, setCurrentTabIndex] = useState(0)
  const [tableData, setTableData] = useState([])

  useEffect(() => {
    setTableData([...appraisalData])
    return () => {}
  }, [appraisalData])

  return (
    <React.Fragment>
      <CardContent>
        <FullWidthTabs
          variant={'scrollable'}
          scrollButtons={'auto'}
          tabs={tableData.map((structure, index) => ({
            key: `${structure.structure?.structure_id}-${index}`,
            label: structure.structure?.structure_name
          }))}
          tabContents={tableData.map((structure, index) => {
            const appraisalTypeCode = appraisalInfo.appraisal_type?.value_list_code
            const structureCode = structure.structure?.structure_type?.value_list_code
            const structureId = structure.structure?.structure_id

            const maxScore = structure.structure?.target_score || null
            const isCompetency = ['STT02', 'STT05'].includes(structureCode)
            const selectedGroupId = structure.selected_assessor_group_id
            const selectedAssessorId = structure.selected_assessor_id
            const assessorStage =
              structure.assessor_stage?.find(item => +item.assessor_id === +selectedAssessorId)
                ?.stage || []

            const fullActualScore = parseFloat(maxScore || 0)
            const fullPercentScore = parseFloat(structure.full_score || 0)
            const percentScore = parseFloat(structure.sum_weight_score || 0)
            const actualScore = (percentScore / fullPercentScore) * fullActualScore

            const structureScoreDisplay = (
              <Typography
                component={Grid}
                container
                alignItems={'baseline'}
                justify={isXs ? 'center' : 'flex-end'}
                wrap={isXs ? 'wrap' : 'nowrap'}
                variant={isXs ? 'body1' : 'h5'}
                align={'right'}>
                <QuarterSpacingGrid item>{translate('score')}:</QuarterSpacingGrid>
                <QuarterSpacingGrid item>{percentScore.toFixed(2)}</QuarterSpacingGrid>
                <Typography
                  component={QuarterSpacingGrid}
                  item
                  variant={isXs ? 'subtitle2' : 'body1'}>
                  ({actualScore.toFixed(2)})
                </Typography>
                <QuarterSpacingGrid item>/</QuarterSpacingGrid>
                <QuarterSpacingGrid item>{fullPercentScore.toFixed(2)}</QuarterSpacingGrid>
                <Typography
                  component={QuarterSpacingGrid}
                  item
                  variant={isXs ? 'subtitle2' : 'body1'}>
                  ({fullActualScore.toFixed(2)})
                </Typography>
              </Typography>
            )

            let competencySelector = null
            if (isCompetency) {
              const { assessorGroupOptions, assessorOptions } = getCompetencyAssessorOptions(
                structure
              )

              const selectedCompetency = getSelectvalue(
                selectedGroupId || null,
                assessorGroupOptions || []
              )
              const selectedEmp = getSelectvalue(
                structure.selected_assessor_id || null,
                assessorOptions || []
              )
              competencySelector = (
                <React.Fragment>
                  <Grid item xs sm md>
                    <Autocomplete
                      options={assessorGroupOptions}
                      label={translate('assessorGroup')}
                      value={selectedCompetency}
                      size={'small'}
                      onChange={(e, value) => {
                        inputChanged({
                          ...structure,
                          selected_assessor_group_id: value?.value || null
                        })
                      }}
                      getOptionLabel={option => option.label || ''}
                      renderInput={params => (
                        <TextField
                          style={{ width: '100%' }}
                          variant={'outlined'}
                          label={translate('assessorGroup')}
                          required
                          {...params}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs sm md>
                    <Autocomplete
                      options={assessorOptions}
                      label={translate('assessor')}
                      value={selectedEmp}
                      size={'small'}
                      onChange={(e, value) => {
                        inputChanged({ ...structure, selected_assessor_id: value?.value || null })
                      }}
                      getOptionLabel={option => option.label || ''}
                      renderInput={params => (
                        <TextField
                          style={{ width: '100%' }}
                          variant={'outlined'}
                          label={translate('assessor')}
                          required
                          {...params}
                        />
                      )}
                    />
                  </Grid>
                </React.Fragment>
              )
            }

            let actionGroups = null
            let actionToButton = null
            if (showActionTo) {
              const actionToLoading = loadingStates.length
              let color = 'primary'
              let handler = () => actionToButtonClicked(structureId)
              let display = translate('action_To')
              if (actionToLoading) {
                color = 'default'
                handler = () => {}
                display = (
                  <Fragment>
                    {translate('loading')}
                    <CircularProgress size={20} style={{ marginLeft: 4 }} />
                  </Fragment>
                )
              }
              actionToButton = (
                <Grid item xs={'auto'}>
                  <Button color={color} clicked={handler} disabled={!!actionToLoading}>
                    {display}
                  </Button>
                </Grid>
              )
            }

            let sumUpButton = null
            if (showSumUp) {
              let color = 'secondary'
              const sumUpLoading = loadingStates.includes('records')
              let handler = sumUpButtonClicked
              let display = translate('sumUp')
              if (sumUpLoading) {
                color = 'default'
                handler = () => {}
                display = (
                  <Fragment>
                    {translate('calLoading')}
                    <CircularProgress size={20} style={{ marginLeft: 4 }} />
                  </Fragment>
                )
              }

              sumUpButton = (
                <Grid item xs={'auto'}>
                  <Button color={color} clicked={handler} disabled={sumUpLoading}>
                    {display}
                  </Button>
                </Grid>
              )
            }

            if (actionToButton || sumUpButton) {
              actionGroups = (
                <Grid container item justify={'flex-end'} alignItems={'center'} spacing={1}>
                  {actionToButton}
                  {sumUpButton}
                </Grid>
              )
            }

            let additionalPanel = null
            if (['STT01'].includes(structureCode) && appraisalTypeCode === 'APT02') {
              additionalPanel = <ParentScore parentScores={structure.parent_score || []} />
            }
            return {
              key: index,
              style: { padding: 0 },
              display: (
                <React.Fragment>
                  <CardContent style={{ padding: '16px 0' }}>
                    <Grid container spacing={2}>
                      {actionGroups}
                      {additionalPanel}
                      <Grid
                        container
                        item
                        alignItems={'center'}
                        justify={'space-between'}
                        spacing={1}>
                        <Grid container spacing={1} item xs={12} sm md lg={6}>
                          {competencySelector}
                        </Grid>
                        <Grid item xs={12} sm md lg={6}>
                          {structureScoreDisplay}
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <StructureTable
                    appraisalInfo={appraisalInfo}
                    openCommentModal={openCommentModal}
                    // competencyTypes={competencyTypes}
                    structureData={structure}
                    calculateButtonClicked={calculateButtonClicked}
                    maxScore={maxScore}
                    isAdmin={isAdmin}
                    groupInputChanged={rowData => {
                      const updatedData = appraisalData.map(st => {
                        if (+st.result_structure_id === +structure.result_structure_id) {
                          const updatedGroups = Object.entries(structure.result_item).reduce(
                            (updated, group) => {
                              const [groupCode, groupAttrs] = group
                              const updatedGroupItems = groupAttrs.items.map(item => {
                                if (+item.item_id === +rowData.item_id) {
                                  return { ...item, ...rowData }
                                }
                                return item
                              })
                              const updatedGroup = { ...groupAttrs, items: updatedGroupItems }
                              return { ...updated, [groupCode]: updatedGroup }
                            },
                            {}
                          )
                          const updatedStructure = { ...structure, result_item: updatedGroups }
                          inputChanged(updatedStructure)
                          return updatedStructure
                        }
                        return st
                      })
                      return updatedData
                    }}
                    inputChanged={rowData => {
                      setTableData(prevState => {
                        const dataCopy = [...prevState]
                        const updatedData = dataCopy.map(st => {
                          if (+st.result_structure_id === +structure.result_structure_id) {
                            const updatedTarget = {
                              ...st,
                              result_item: st.result_item?.map(item => {
                                if (+item.result_item_id === +rowData.result_item_id) {
                                  return { ...item, ...rowData }
                                }
                                return item
                              })
                            }
                            inputChanged(updatedTarget)
                            return updatedTarget
                          }
                          return st
                        })
                        return updatedData
                      })
                    }}
                  />
                  <AppraisalActionControls
                    formInputChanged={updatedData => inputChanged(updatedData)}
                    form={structure}
                    submitted={() => submitButtonClicked(structure.result_structure_id)}
                    cancelled={cancelButtonClicked}
                    stageList={isCompetency ? assessorStage || [] : structure?.stage || []}
                    savedList={savedList}
                    loading={loadingStates.includes('saveAppraisal')}
                  />
                </React.Fragment>
              )
            }
          })}
          currentTabIndex={currentTabIndex}
          tabChanged={(e, newIndex) => setCurrentTabIndex(newIndex)}
        />
      </CardContent>
    </React.Fragment>
  )
}

const QuarterSpacingGrid = withStyles(theme => ({
  root: { margin: parseInt(theme.spacing(1) / 4) }
}))(Grid)

export default withTranslate(StructureTabs)
